import { createContext, Fragment, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { en, type Dict } from "./en/index.ts";
import { browserLocale, isLocale, localeInfo, type Locale } from "./locales.ts";

import "./i18n.css";
export * from "./locales.ts";
export { fmt, plural, rich } from "./format.tsx";
export type { Dict } from "./en/index.ts";

const STORE_KEY = "icn_lang";

// Other languages are separate chunks, loaded when picked (src/i18n/<code>/index.ts exports `dict`).
const modules = import.meta.glob<{ dict: Dict }>(["./*/index.ts", "!./en/index.ts"]);
async function load(lang: Locale): Promise<Dict> {
  if (lang === "en") return en;
  const mod = modules[`./${lang}/index.ts`];
  return mod ? (await mod()).dict : en;
}

function initialLocale(): Locale {
  try {
    const saved = localStorage.getItem(STORE_KEY);
    if (isLocale(saved)) return saved;
  } catch {
    /* storage unavailable */
  }
  return browserLocale();
}

/** The active language for code outside React (formatters, error messages). Set before the app renders. */
let current: { lang: Locale; dict: Dict } = { lang: "en", dict: en };
export const tr = (): Dict => current.dict;
export const currentLocale = (): Locale => current.lang;
/** BCP 47 tag for Intl (dates, numbers). */
export const intlLocale = (): string => localeInfo(current.lang).html;

type Ctx = { lang: Locale; t: Dict; setLang: (l: Locale) => void };
const I18nCtx = createContext<Ctx>({ lang: "en", t: en, setLang: () => {} });

/** Loads the chosen language, sets <html lang dir>, and remounts the app when the language changes. */
export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Locale>(initialLocale);
  const [loaded, setLoaded] = useState<{ lang: Locale; dict: Dict } | null>(lang === "en" ? { lang, dict: en } : null);

  useEffect(() => {
    let live = true;
    load(lang).then((dict) => live && setLoaded({ lang, dict }), () => live && setLoaded({ lang: "en", dict: en }));
    return () => {
      live = false;
    };
  }, [lang]);

  useEffect(() => {
    if (!loaded) return;
    const info = localeInfo(loaded.lang);
    document.documentElement.lang = info.html;
    document.documentElement.dir = info.rtl ? "rtl" : "ltr";
  }, [loaded]);

  const setLang = useCallback((l: Locale) => {
    try {
      localStorage.setItem(STORE_KEY, l);
    } catch {
      /* ignore */
    }
    setLangState(l);
  }, []);

  if (!loaded) return null; // a non-English dictionary is loading (one small request)
  current = loaded;
  return (
    <I18nCtx.Provider value={{ lang: loaded.lang, t: loaded.dict, setLang }}>
      <Fragment key={loaded.lang}>{children}</Fragment>
    </I18nCtx.Provider>
  );
}

/** The dictionary for the active language: `const t = useT(); t.payouts.title`. */
export const useT = (): Dict => useContext(I18nCtx).t;
export const useLang = () => {
  const { lang, setLang } = useContext(I18nCtx);
  return { lang, setLang };
};
